import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, interval } from 'rxjs';

import { DataService } from './data.service';
import {
  RemoteControlPressButtonDataModel,
  RemoteControlDisplayDataModel,
  RemoteButton,
  RemoteControlState,
} from '../remote-control.model';

@Injectable({ providedIn: 'root' })
export class QueueService {
  private buttonQueue: RemoteButton[] = [];
  private lastSendedButton: RemoteButton | null = null;

  private state: RemoteControlState = {
    terminalConnected: true,
    connectionErrors: false,
    sendingButton: false,
    readingScreen: false,
    queueButtons: 0,
    ready: false,
  };

  private displayLinesSubject = new Subject<string[]>();
  public displayLines$ = this.displayLinesSubject.asObservable();

  private buttonQueueSubject = new BehaviorSubject<RemoteButton[]>([]);
  public buttonQueue$ = this.buttonQueueSubject.asObservable();

  private lastSendedButtonSubject = new BehaviorSubject<RemoteButton | null>(
    null
  );
  public lastSendedButton$ = this.lastSendedButtonSubject.asObservable();

  private stateSubject = new BehaviorSubject<RemoteControlState>(this.state);
  public state$ = this.stateSubject.asObservable();

  private intervalSubscription: Subscription;

  private pendingInterval = 6000;
  private normalInterval = 15000;
  private afterErrorInterval = 20000;

  constructor(private remoteControlService: DataService) {}

  public updateState(part: Partial<RemoteControlState>): void {
    this.state = { ...this.state, ...part };
    this.stateSubject.next({ ...this.state });
  }

  public enqueueButton(button: RemoteButton, terminal_vid: string): void {
    this.buttonQueue.push({ ...button, sending: false });
    this.buttonQueueSubject.next([...this.buttonQueue]);

    if (
      this.buttonQueue.length === 1 &&
      this.state.queueButtons === 0 &&
      !this.state.sendingButton &&
      !this.state.readingScreen
    ) {
      this.processQueue(terminal_vid);
    }
  }

  public startPolling(terminal_vid: string): void {
    this.getDisplay(terminal_vid);
  }

  public stopPolling(): void {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
  }

  public getState(): RemoteControlState {
    return { ...this.state };
  }

  private processQueue(terminal_vid: string): void {
    if (
      !this.state.terminalConnected ||
      this.state.queueButtons > 0 ||
      this.state.sendingButton ||
      this.state.readingScreen
    ) {
      return;
    }
    if (this.buttonQueue.length === 0) {
      return;
    }

    const next = this.buttonQueue[0];
    next.sending = true;
    this.updateState({ sendingButton: true, ready: false });
    this.buttonQueueSubject.next([...this.buttonQueue]);

    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }

    this.remoteControlService.pressButton(terminal_vid, next.id).subscribe(
      (resp: RemoteControlPressButtonDataModel) => {
        this.updateState({
          connectionErrors: false,
          sendingButton: false,
          queueButtons: resp.pending_buttons,
        });
        this.lastSendedButton = this.buttonQueue.shift();
        this.buttonQueueSubject.next([...this.buttonQueue]);
        this.lastSendedButtonSubject.next(this.lastSendedButton);

        if (
          this.buttonQueue.length > 0 &&
          this.state.queueButtons === 0 &&
          !this.state.sendingButton
        ) {
          setTimeout(() => this.processQueue(terminal_vid), 200);
        } else if (
          this.buttonQueue.length === 0 &&
          this.state.queueButtons === 0
        ) {
          this.updateState({ ready: true });
        }
        this.reloadSubscription(terminal_vid);
      },
      () => {
        this.updateState({
          sendingButton: false,
          queueButtons: 0,
          ready: false,
          connectionErrors: true,
        });
        setTimeout(() => {
          this.updateState({ connectionErrors: false });
          this.getDisplay(terminal_vid);
        }, this.afterErrorInterval);
      }
    );
  }

  private getDisplay(terminal_vid: string): void {
    this.updateState({ readingScreen: true, ready: false });
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }

    this.remoteControlService.getDisplay(terminal_vid).subscribe(
      (resp: RemoteControlDisplayDataModel) => {
        this.updateState({
          connectionErrors: false,
          readingScreen: false,
          queueButtons: resp.pending_buttons,
        });
        this.displayLinesSubject.next(resp.display_lines.map((l) => l.texto));

        if (this.state.queueButtons === 0) {
          this.updateState({ ready: this.buttonQueue.length === 0 });
          if (this.buttonQueue.length > 0 && !this.state.sendingButton) {
            this.processQueue(terminal_vid);
          }
        } else {
          this.updateState({ ready: false });
        }
        this.reloadSubscription(terminal_vid);
      },
      () => {
        this.updateState({
          readingScreen: false,
          queueButtons: 0,
          ready: false,
          connectionErrors: true,
        });
        setTimeout(() => {
          this.updateState({ connectionErrors: false });
          this.getDisplay(terminal_vid);
        }, this.afterErrorInterval);
      }
    );
  }

  private reloadSubscription(terminal_vid: string): void {
    if (this.intervalSubscription) {
      this.intervalSubscription.unsubscribe();
    }
    const currentInterval =
      this.state.queueButtons > 0 ? this.pendingInterval : this.normalInterval;
    this.intervalSubscription = interval(currentInterval).subscribe(() =>
      this.getDisplay(terminal_vid)
    );
  }
}
